import { trackAmplitudeEvent } from '../config/amplitude';
import type { ChecklistItem, ChecklistItemId, ChecklistTier } from '../services/onboardingService';

// Marketing's Amplitude tracking plan uses slightly different slugs than the
// internal ChecklistItemId values. Keep StableTrack's IDs unchanged (they
// touch DB rows, backend logic, and frontend state) and translate at the
// analytics boundary instead. If marketing ever renames an event property,
// this is the only place that needs to change.
const STEP_NAME_BY_ID: Record<ChecklistItemId, string> = {
  add_first_client: 'add_first_client',
  add_first_horse: 'add_first_horse',
  schedule_first_appointment: 'schedule_first_appointment',
  add_inventory_item: 'add_first_inventory_item',
  complete_first_soap: 'complete_first_soap_note',
  send_first_discharge: 'send_first_discharge_summary',
  create_first_invoice: 'create_first_invoice',
  record_first_payment: 'record_first_payment',
  add_clinic_logo: 'add_clinic_logo',
  set_tax_rate: 'set_default_tax_rate',
  connect_stripe: 'connect_stripe',
  set_up_service_pricing: 'set_up_service_pricing',
  set_up_appointment_types: 'set_up_appointment_types',
  set_up_soap_templates: 'create_soap_template',
  invite_team: 'invite_team',
  enable_offline: 'set_up_offline_access',
};

const PHASE_BY_TIER: Record<ChecklistTier, string> = {
  data: 'get_data_in',
  workflow: 'complete_workflow',
  setup: 'configure_clinic',
};

// Position in the 16-step ladder. Order matches the backend CHECKLIST_CATALOG
// (TIER_ORDER = [data, workflow, setup], then catalog position within each
// tier). This must stay in sync with backend src/modules/onboarding/onboarding.service.ts.
const STEP_INDEX_BY_ID: Record<ChecklistItemId, number> = {
  add_first_client: 1,
  add_first_horse: 2,
  schedule_first_appointment: 3,
  add_inventory_item: 4,
  complete_first_soap: 5,
  send_first_discharge: 6,
  create_first_invoice: 7,
  record_first_payment: 8,
  add_clinic_logo: 9,
  set_tax_rate: 10,
  connect_stripe: 11,
  set_up_service_pricing: 12,
  set_up_appointment_types: 13,
  set_up_soap_templates: 14,
  invite_team: 15,
  enable_offline: 16,
};

export type CompletionType = 'completed' | 'dismissed';

export function trackOnboardingStepCompleted(
  item: ChecklistItem,
  completionType: CompletionType,
): void {
  trackAmplitudeEvent('onboarding_step_completed', {
    step_name: STEP_NAME_BY_ID[item.id],
    step_phase: PHASE_BY_TIER[item.tier],
    step_index: String(STEP_INDEX_BY_ID[item.id]),
    completion_type: completionType,
  });
}
